import * as React from 'react';
import {useCallback, useState, useMemo} from 'react';
import {Card, Loader} from 'semantic-ui-react';
import {RunHistoryRow} from '../types/run';
import {runLink} from '../util/runhelpers';

import MessageMediaNotFound from './MessageMediaNotFound';

import memoize from 'memoize-one';
import {useLoadFile, FileInfo} from '../util/requests';
import {MediaCardProps} from './MediaCard';
import {makeCaptions, useStepWithRollback, labelComponent} from '../util/media';
import Viewer3D from './Viewer3D';
import PointCloud from './PointCloud';
import makeComp from '../util/profiler';

export const Object3DCard: React.FunctionComponent<MediaCardProps> = makeComp(
  props => {
    const {run, mediaKey, mediaIndex, runSignature, height, width} = props;

    const {currentStep, stepData, failedToLoad, rollback} = useStepWithRollback(
      props
    );

    let mediaPath = getMedia(stepData, mediaKey, mediaIndex);

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    const [fileData, setFileData] = useState<File>();
    const [directUrl, setDirectUrl] = useState<string>();

    const onSuccess = useCallback((file: File, fileInfo: FileInfo) => {
      setFileData(file);
      setDirectUrl(fileInfo.directUrl);
    }, []);

    // Use empty string when we have null and skip the request
    mediaPath = mediaPath || '';
    // ! Warning: The callbacks passed to useLoadFile must stay
    // equal by reference or it will retrigger a new file download.
    const loading = useLoadFile(runSignature, mediaPath, {
      responseType: 'file',
      onSuccess,
      fallback: rollback,
      skip: mediaPath === '' || failedToLoad,
    });

    let Media3DComponent;
    if (mediaPath.slice(-9) === '.pts.json') {
      Media3DComponent = PointCloud;
    } else {
      Media3DComponent = Viewer3D;
    }

    const currentMediaMetadata = stepData && stepData[mediaKey];

    const mediaGroupingCount =
      (currentMediaMetadata && currentMediaMetadata.grouping) || 1;

    const captions = useMemo(() => {
      return makeCaptions(currentMediaMetadata, mediaIndex, mediaGroupingCount);
    }, [currentMediaMetadata, mediaIndex, mediaGroupingCount]);

    const showMetadataInline = width > 100; // if this is false, metadata will appear in a popup on the image

    return (
      <Card className="object3D-card" style={{width}}>
        {labelComponent(props, currentStep, titleLink)}

        {!mediaPath ? (
          <MessageMediaNotFound
            mediaKey={mediaKey}
            stepIndex={currentStep}
            mediaIndex={mediaIndex}
            mediaType="object3D"
          />
        ) : loading || directUrl == null || fileData == null ? (
          <div style={{width, height}}>
            <Loader active />
          </div>
        ) : (
          <div
            style={{
              width,
              height,
            }}>
            <Media3DComponent
              width={width}
              height={height}
              url={directUrl}
              fileData={fileData}
              controls={props.controls}
            />
          </div>
        )}
        {/* CAPTIONS */}
        {showMetadataInline && captions.length > 0 && (
          <div className="image-card-caption">{captions}</div>
        )}
      </Card>
    );
  },
  {id: 'Object3DCard'}
);

// Get the new-style Media object or null if there isn't one.
const getMedia = memoize(
  (
    stepData: RunHistoryRow | undefined,
    mediaKey: string,
    index: number
  ): string | null => {
    if (!stepData || !stepData[mediaKey]) {
      return null;
    }
    const stepMedia = stepData[mediaKey];
    if (stepMedia._type === 'object3D') {
      if (stepMedia.filenames && stepMedia.filenames[index]) {
        return `media/object3D/${stepMedia.filenames[index]}`;
      } else {
        return null;
      }
    } else if (stepMedia._type === 'object3D-file') {
      return stepMedia.path;
    } else {
      return null;
    }
  }
);

export default Object3DCard;
